import React, { useState } from "react";
import { MdCheck, MdDeleteOutline, MdOutlineMail, MdClose, MdNotificationsNone } from "react-icons/md";
import { LuBell } from "react-icons/lu";
import NotificationModal from "./NotificationModal";
import "../../Styles/NotificationPane.css";

export default function NotificationPane({ isOpen, onClose, notifications = [], unreadCount = 0, isLoading = false, isLoadingMore = false, hasMore = false, loadMore, markAsRead, markAsUnread, markAllAsRead, deleteNotification }) {
    const [selectedNotification, setSelectedNotification] = useState(null);
    return (
        <>
            <div
                className={`offcanvas offcanvas-end notif-offcanvas ${isOpen ? "show" : ""}`}
                style={{ visibility: isOpen ? "visible" : "hidden", zIndex: 1050 }}
                aria-hidden={!isOpen}
            >
                <div className="notif-header d-flex flex-wrap justify-content-between align-items-center px-4 py-3">
                    <div className="d-flex align-items-center gap-3 notif-header-main">
                        <span className={`notif-bell-badge ${unreadCount > 0 ? "has-unread" : ""}`}>
                            <LuBell size={19} strokeWidth={2.5} />
                        </span>
                        <div className="notif-header-titles">
                            <h5 className="notif-title mb-0 fw-bold text-white d-flex align-items-center gap-2">
                                <span className="text-truncate" style={{ minWidth: 0 }}>Notifications</span>
                                {unreadCount > 0 && <span className="notif-count-pill">{unreadCount}</span>}
                            </h5>
                            <small className="notif-header-sub">
                                {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? "s" : ""}` : "You're all caught up"}
                            </small>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 notif-header-actions">
                        {unreadCount > 0 && (
                            <button type="button" className="notif-mark-all-btn" onClick={markAllAsRead}>
                                Mark all read
                            </button>
                        )}
                        <button
                            type="button"
                            className="notif-close-btn"
                            onClick={onClose}
                            aria-label="Close notifications"
                        >
                            <MdClose size={18} />
                        </button>
                    </div>
                </div>
                <div className="offcanvas-body notif-body p-0">
                    {isLoading ? (
                        <div className="notif-empty">
                            <span className="spinner-border spinner-border-sm" style={{ color: "var(--hc-gradient-mid, #3c9fa2)" }} role="status" aria-hidden="true" />
                            <p className="mb-0 mt-2 text-muted small">Loading notifications...</p>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="notif-empty">
                            <div className="notif-empty-icon">
                                <MdNotificationsNone size={30} />
                            </div>
                            <p className="mb-1 fw-semibold text-dark">No notifications yet</p>
                            <small className="text-muted">We'll let you know when something new comes in</small>
                        </div>
                    ) : (
                        <div className="notif-list">
                            {notifications.map((notification, index) => (
                                <div
                                    key={notification.id}
                                    className={`notif-item ${!notification.isRead ? "unread" : "read"}`}
                                    style={{ animationDelay: `${Math.min(index, 12) * 35}ms` }}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => {
                                        setSelectedNotification(notification);
                                        if (!notification.isRead) markAsRead(notification.id);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key !== "Enter" && e.key !== " ") return;
                                        e.preventDefault();
                                        setSelectedNotification(notification);
                                        if (!notification.isRead) markAsRead(notification.id);
                                    }}
                                >
                                    <div className="notif-item-icon">
                                        <LuBell size={16} />
                                    </div>
                                    <div className="notif-item-body">
                                        <div className="d-flex w-100 justify-content-between align-items-center gap-2 notif-item-title-row">
                                            <h6 className={`notif-item-title mb-1 ${!notification.isRead ? "fw-bold" : "fw-semibold"}`}>
                                                {notification.title}
                                            </h6>
                                            <div className="notif-item-meta">
                                                <span className="notif-item-time">
                                                    {new Date(notification.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                </span>
                                                <div className="notif-actions" onClick={(e) => e.stopPropagation()}>
                                                    {notification.isRead ? (
                                                        <button
                                                            className="notif-action-btn read-action"
                                                            title="Mark as unread"
                                                            onClick={() => markAsUnread(notification.id)}
                                                        >
                                                            <MdOutlineMail size={15} />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            className="notif-action-btn read-action"
                                                            title="Mark as read"
                                                            onClick={() => markAsRead(notification.id)}
                                                        >
                                                            <MdCheck size={16} />
                                                        </button>
                                                    )}
                                                    <button
                                                        className="notif-action-btn delete-action"
                                                        title="Delete"
                                                        onClick={() => deleteNotification(notification.id)}
                                                    >
                                                        <MdDeleteOutline size={15} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                        <p className="notif-item-message mb-0">
                                            {notification.message}
                                        </p>
                                    </div>
                                </div>
                            ))}
                            {hasMore && (
                                <button
                                    type="button"
                                    className="notif-load-more-btn"
                                    onClick={loadMore}
                                    disabled={isLoadingMore}
                                >
                                    {isLoadingMore ? (
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                    ) : "Load more"}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {isOpen ? (
                <button
                    type="button"
                    className="offcanvas-backdrop fade show border-0 p-0 notif-backdrop"
                    onClick={onClose}
                    aria-label="Close notifications"
                    style={{ zIndex: 1040 }}
                />
            ) : null}

            <NotificationModal
                isOpen={selectedNotification !== null}
                notification={selectedNotification}
                onClose={() => setSelectedNotification(null)}
                onDelete={deleteNotification}
                onMarkAsRead={markAsRead}
                onMarkAsUnread={markAsUnread}
            />
        </>
    );
}
